import React from 'react';
import { useQuery } from '@apollo/client';
import Hero from '../../common/Hero';
import ImageSlider from '../../common/Image-slider';
import styled from 'styled-components';
import castleImage from '../../../assets/castle.png';
import gardensImage from '../../../assets/gardens.png';
import retailImage from '../../../assets/retail.png';
import playgroundImage from '../../../assets/play.png';
import WhatsOn from '../../common/What-on';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ColumnX2 from '../../common/Column-x2';
import retailVideo from '../../../assets/retail-village.mp4';
import ImageSlant from '../../common/image-slant';
import Footer from '../../Footer/Footer';
import { gql } from '@apollo/client';
import { processEvents, GET_EVENTS_QUERY, combineAndSortEvents } from '../../../utils/eventUtils';
import { useNavigate } from 'react-router-dom';
import SEO from '../../common/SEO';
import {
  getAnimalParkOpeningDisplay,
  isAnimalParkOpenAllDaysThisWeek,
} from '../../../utils/openingHoursUtils';

import farmshopImage from '../../../assets/farmshop1.webp';
import bistroImage from '../../../assets/bistro.webp';
import animalParkImage from '../../../assets/animal.webp';
import pizzaImage from '../../../assets/pizza.webp';
import balloonImage from '../../../assets/ballonBar.webp';
import shopDineImage from '../../../assets/retail3.webp';
import retailVillageImage from '../../../assets/retail3.webp';
import goatImage from '../../../assets/farm1.webp';

// Helper function to get the 3rd Sunday of a given month
const getThirdSunday = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const firstSunday = firstDay.getDay() === 0 ? 1 : (7 - firstDay.getDay() + 1);
  const thirdSunday = firstSunday + 14;
  return new Date(year, month, thirdSunday);
};

// Generate Antiques Market event (single event covering all dates)
const generateAntiquesMarketEvents = () => {
  const year = 2026;
  // Get first date (April) and last date (September)
  const firstDate = getThirdSunday(year, 3); // April
  const lastDate = getThirdSunday(year, 8); // September
  
  return [{
    id: 'antiques-artisan-market-2026',
    title: 'Vintage & Craft Market',
    subtitle: '3rd Sunday Every Month | April - September',
    date: '3rd Sunday Every Month | April - September',
    description: 'A monthly celebration of craftsmanship, character and timeless finds. Browse curated stalls featuring local artists, makers, antiques and collectables. Free entry.',
    image: retailVillageImage,
    location: 'RETAIL VILLAGE',
    link: '/events/antiques-artisan-market',
    linkText: 'Discover the Market',
    eventTags: ['market', 'family', 'free'],
    eventDetails: {
      eventDate: firstDate.toISOString().split('T')[0],
      eventEndDate: lastDate.toISOString().split('T')[0],
      eventTime: '10:00 - 16:00',
      eventTags: ['market', 'family', 'outdoor', 'food'],
    }
  }];
};



const HomeContainer = styled.div`
  min-height: 100vh;
  padding-top: 158px;
`;

const HomeSlider = styled(ImageSlider)`
  @media (max-width: 768px) {
    margin-top: 7rem;
  }
`;

const StyledNewsletterSignUp = styled(NewsletterSignUp)`
  margin: 30px 0;
`;

const GET_RETAIL_SETTINGS = gql`
  query GetSiteSettings {
    siteSettings(where: {title: "Global Site Settings"}) {
      nodes {
        title
        siteSettings {
          retailVillageSection {
            heroSection {
              welcomeText
              mainTitle
              subtitle
              heroVideo {
                node {
                  sourceUrl
                }
              }
            }
            infoColumns {
              column1 {
                title
                days
                times
                note
              }
              column2 {
                title
                days
                times
                note
              }
              column3 {
                title
                days
                times
                note
              }
            }
            sliderContent {
              slide1 {
                title
                image {
                  node {
                    sourceUrl
                  }
                }
              }
              slide2 {
                title
                image {
                  node {
                    sourceUrl
                  }
                }
              }
              slide3 {
                title
                image {
                  node {
                    sourceUrl
                  }
                }
              }
              slide4 {
                title
                image {
                  node {
                    sourceUrl
                  }
                }
              }
            }
            eventsSection {
              title
              eventsContent {
                title
                date
                description
                link
                linkText
                image {
                  node {
                    sourceUrl
                  }
                }
              }
            }
            columnX2Section {
              title
              description
              buttonText
            }
            imageSlantSections {
              title
              description
              buttonText
              imagePosition
              image {
                node {
                  sourceUrl
                }
              }
            }
          }
        }
      }
    }
  }
`;

const RetailVillage = () => {
  const { loading: settingsLoading, error: settingsError, data: settingsData } = useQuery(GET_RETAIL_SETTINGS);
  const navigate = useNavigate();
  
  // Add debug logging
  console.log('Settings Loading:', settingsLoading);
  console.log('Settings Error:', settingsError);
  console.log('Settings Data:', settingsData);

  // Get the settings data
  const retailSettings = settingsData?.siteSettings?.nodes?.[0]?.siteSettings?.retailVillageSection || {};
  console.log('Retail Settings:', retailSettings);

  // Fetch events data
  const { loading: eventsLoading, error: eventsError, data: eventsData } = useQuery(GET_EVENTS_QUERY);

  // Add logging for raw events data
  console.log('Raw Events Data:', eventsData?.events?.nodes);

  const animalParkOpeningDisplay = getAnimalParkOpeningDisplay();
  const animalParkSliderTitle = isAnimalParkOpenAllDaysThisWeek()
    ? 'Animal Park — Open Daily'
    : 'Animal Park — Sat–Sun';

  // Update default slider content with proper navigation
  const defaultSliderContent = [
    {
      image: animalParkImage,
      title: animalParkSliderTitle,
      action: () => navigate('/retail-village/animal-park')
    },
    {
      image: farmshopImage,
      title: "Farm Shop",
      action: () => navigate('/retail-village/shops/farm-shop')
    },
    {
      image: bistroImage,
      title: "Belvoir Bistro",
      action: () => navigate('/retail-village/dining/belvoir-bistro')
    },
    {
      image: balloonImage,
      title: "Balloon Bar",
      action: () => navigate('/retail-village/dining/balloon-bar')
    },
    {
      image: pizzaImage,
      title: "Woodfired Pizza",
      action: () => navigate('/retail-village/dining/woodfired-pizza')
    }
  ];

  // Update slider content actions
  const sliderContent = retailSettings.sliderContent
    ? [
        retailSettings.sliderContent.slide1 && {
          image: retailSettings.sliderContent.slide1.image?.node?.sourceUrl || retailImage,
          title: retailSettings.sliderContent.slide1.title || animalParkSliderTitle,
          action: () => navigate('/retail-village/animal-park')
        },
        retailSettings.sliderContent.slide2 && {
          image: retailSettings.sliderContent.slide2.image?.node?.sourceUrl || retailImage,
          title: retailSettings.sliderContent.slide2.title || "Farm Shop",
          action: () => navigate('/retail-village/shops/farm-shop')
        },
        retailSettings.sliderContent.slide3 && {
          image: retailSettings.sliderContent.slide3.image?.node?.sourceUrl || retailImage,
          title: retailSettings.sliderContent.slide3.title || "Belvoir Bistro",
          action: () => navigate('/retail-village/dining/belvoir-bistro')
        },
        retailSettings.sliderContent.slide4 && {
          image: retailSettings.sliderContent.slide4.image?.node?.sourceUrl || retailImage,
          title: retailSettings.sliderContent.slide4.title || "Balloon Bar",
          action: () => navigate('/retail-village/dining/balloon-bar')
        }
      ].filter(Boolean)
    : defaultSliderContent;

  // Generate Antiques Market events
  const antiquesMarketEvents = generateAntiquesMarketEvents();
  
  // Process WordPress events data into the format your component expects
  const wordpressRetailEvents = eventsLoading ? [] : 
    eventsData && eventsData.events && eventsData.events.nodes ? 
    processEvents(eventsData.events.nodes, {}, 8).filter(event => {
      // More flexible matching for Retail Village events
      const location = event.location?.toLowerCase() || '';
      const title = event.title?.toLowerCase() || '';
      return location.includes('retail') || 
             location.includes('balloon bar') || 
             location.includes('bistro') || 
             location.includes('farm shop') || 
             location.includes('woodfired pizza') ||
             location.includes('retail village') ||
             location === 'retail village' ||
             title.includes('balloon bar') ||
             title.includes('bistro') ||
             title.includes('farm shop') ||
             title.includes('woodfired pizza');
    }) : [];
  
  // Combine Antiques Market with WordPress retail events and sort by date
  const allRetailEvents = [...antiquesMarketEvents, ...wordpressRetailEvents];
  const retailEvents = allRetailEvents.sort((a, b) => {
    const dateA = a.eventDetails?.eventDate ? new Date(a.eventDetails.eventDate) : new Date(0);
    const dateB = b.eventDetails?.eventDate ? new Date(b.eventDetails.eventDate) : new Date(0);
    return dateA - dateB;
  });

  const otherEvents = eventsLoading ? [] : 
    eventsData && eventsData.events && eventsData.events.nodes ? 
    processEvents(eventsData.events.nodes, {}, 8).filter(event => {
      // Detailed debug logging
      console.log('Other Event Debug:', {
        title: event.title,
        location: event.location,
        rawLocation: event.location,
        processedLocation: event.location?.toLowerCase(),
        matches: !event.location?.toLowerCase().includes('retail') && 
                !event.location?.toLowerCase().includes('parkland') &&
                !event.location?.toLowerCase().includes('balloon bar') && 
                !event.location?.toLowerCase().includes('bistro') && 
                !event.location?.toLowerCase().includes('farm shop') && 
                !event.location?.toLowerCase().includes('woodfired pizza') &&
                !event.location?.toLowerCase().includes('retail village') &&
                event.location?.toLowerCase() !== 'retail village' &&
                !event.title?.toLowerCase().includes('balloon bar') &&
                !event.title?.toLowerCase().includes('bistro') &&
                !event.title?.toLowerCase().includes('farm shop') &&
                !event.title?.toLowerCase().includes('woodfired pizza'),
        allEventData: event
      });
      
      // More flexible matching for other events
      const location = event.location?.toLowerCase() || '';
      const title = event.title?.toLowerCase() || '';
      return !location.includes('retail') && 
             !location.includes('parkland') &&
             !location.includes('balloon bar') && 
             !location.includes('bistro') && 
             !location.includes('farm shop') && 
             !location.includes('woodfired pizza') &&
             !location.includes('retail village') &&
             location !== 'retail village' &&
             !title.includes('balloon bar') &&
             !title.includes('bistro') &&
             !title.includes('farm shop') &&
             !title.includes('woodfired pizza');
    }) : [];

  // Add logging for the final filtered events
  console.log('Final Retail Events:', retailEvents);
  console.log('Final Other Events:', otherEvents);

  /** Match Animal Park page: open daily; fix legacy "Open" / CMS copy. */
  const normalizeAnimalParkInfoColumn = (columns) => {
    if (!Array.isArray(columns)) return columns;
    const apDays = animalParkOpeningDisplay.days === 'Daily'
      ? 'Daily'
      : 'Sat - Sun';
    const apTimes = animalParkOpeningDisplay.times;
    return columns.map((col) => {
      if (!col?.title || !/^animal\s*park$/i.test(String(col.title).trim())) {
        return col;
      }
      if (isAnimalParkOpenAllDaysThisWeek()) {
        return {
          ...col,
          items: [apDays, apTimes],
          note: animalParkOpeningDisplay.note,
        };
      }
      const d = col.items?.[0];
      const t = col.items?.[1];
      if (typeof d === 'string' && /^open$/i.test(d.trim())) {
        return {
          ...col,
          items: [apDays, t && String(t).trim() ? t : apTimes],
        };
      }
      return col;
    });
  };

  // Update hero buttons with proper navigation
  const defaultHeroInfo = {
    videoSrc: retailVideo,
    welcomeText: "Welcome to",
    mainTitle: "Belvoir Retail Village",
    subtitle: "SHOPPING, DINING & WILDLIFE IN HISTORIC SURROUNDINGS",
    buttons: [
      {
        text: "Animal Park",
        onClick: () => navigate('/retail-village/animal-park')
      },
      {
        text: "View Shops",
        onClick: () => navigate('/retail-village/shops')
      }
    ],
    infoColumns: [
      {
        title: "Animal Park",
        items: [
          animalParkOpeningDisplay.days === 'Daily' ? 'Daily' : 'Sat - Sun',
          animalParkOpeningDisplay.times,
        ],
        note: animalParkOpeningDisplay.note,
      },
      {
        title: "Shopping Hours",
        items: ["Mon - Sun", "09:30 - 17:00"]
      },
      {
        title: "Balloon Bar",
        items: ["Closed",""]
      }
    ]
  };

  // Use settings data for hero info if available
  const resolvedInfoColumns = normalizeAnimalParkInfoColumn(
    retailSettings.infoColumns
      ? [
          retailSettings.infoColumns.column1 && {
            title: retailSettings.infoColumns.column1.title,
            items: [retailSettings.infoColumns.column1.days, retailSettings.infoColumns.column1.times],
            note: retailSettings.infoColumns.column1.note
          },
          retailSettings.infoColumns.column2 && {
            title: retailSettings.infoColumns.column2.title,
            items: [retailSettings.infoColumns.column2.days, retailSettings.infoColumns.column2.times],
            note: retailSettings.infoColumns.column2.note
          },
          retailSettings.infoColumns.column3 && {
            title: retailSettings.infoColumns.column3.title,
            items: [retailSettings.infoColumns.column3.days, retailSettings.infoColumns.column3.times],
            note: retailSettings.infoColumns.column3.note
          }
        ].filter(Boolean)
      : defaultHeroInfo.infoColumns
  );

  const heroInfo = {
    videoSrc: retailSettings.heroSection?.heroVideo?.node?.sourceUrl || retailVideo,
    welcomeText: retailSettings.heroSection?.welcomeText || "Welcome to",
    mainTitle: retailSettings.heroSection?.mainTitle || "Belvoir Retail Village",
    subtitle: retailSettings.heroSection?.subtitle || "SHOPPING, DINING & WILDLIFE IN HISTORIC SURROUNDINGS",
    buttons: defaultHeroInfo.buttons,
    infoColumns: resolvedInfoColumns
  };

  // Get column x2 section data from settings
  const columnX2Title = retailSettings.columnX2Section?.title || "A Unique Shopping Experience";
  const columnX2Description = retailSettings.columnX2Section?.description || 
    "Discover our carefully curated selection of boutique shops, artisan food producers, and local craftspeople. The Retail Village offers a distinctive shopping experience in the historic grounds of Belvoir Castle.";
  const columnX2ButtonText = retailSettings.columnX2Section?.buttonText || "View Shops";

  // Get image slant sections from settings
  const imageSlantSections = retailSettings.imageSlantSections?.length > 0
    ? retailSettings.imageSlantSections
    : [
        {
          title: "Meet Our Animals",
          description: "Discover our diverse collection of animals at the Animal Park, from exotic species to local wildlife. A perfect day out for families and nature enthusiasts.",
          buttonText: "Visit Animal Park",
          imagePosition: "left",
          image: { node: { sourceUrl: goatImage } }
        },
        {
          title: "Shopping & Dining",
          description: "Explore our carefully curated selection of shops and enjoy a variety of dining options, from casual bites to elegant dining experiences.",
          buttonText: "View Shops",
          imagePosition: "right",
          image: { node: { sourceUrl: shopDineImage } }
        }
      ];

  return (
    <HomeContainer>
      <SEO 
        title="Belvoir Retail Village - Shopping, Dining & Wildlife"
        description="Discover the Belvoir Retail Village, offering unique shopping experiences, delicious dining options, and wildlife encounters in the historic grounds of Belvoir Castle."
        keywords="Belvoir Retail Village, shopping, dining, wildlife, Belvoir Castle, Leicestershire, boutique shops, restaurants, animal park"
        canonicalUrl="https://www.belvoircastle.com/retail-village"
        ogImage={retailImage}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ShoppingCenter",
          "name": "Belvoir Retail Village",
          "description": "Discover the Belvoir Retail Village, offering unique shopping experiences, delicious dining options, and wildlife encounters in the historic grounds of Belvoir Castle.",
          "image": retailImage,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Belvoir Castle",
            "addressLocality": "Grantham",
            "addressRegion": "Leicestershire",
            "postalCode": "NG32 1PE",
            "addressCountry": "UK"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "09:30",
            "closes": "17:30"
          },
          "containsPlace": [
            {
              "@type": "Restaurant",
              "name": "Belvoir Bistro",
              "servesCuisine": "British"
            },
            {
              "@type": "Restaurant",
              "name": "Balloon Bar",
              "servesCuisine": "Bar & Grill"
            },
            {
              "@type": "Restaurant",
              "name": "Woodfired Pizza",
              "servesCuisine": "Italian"
            }
          ],
          "sameAs": [
            "https://www.facebook.com/belvoircastle",
            "https://www.instagram.com/belvoircastle",
            "https://twitter.com/belvoircastle"
          ]
        }}
      />
      <Hero {...heroInfo} />
      <HomeSlider slides={sliderContent} />
      {eventsLoading ? (
        <p>Loading events...</p>
      ) : eventsError ? (
        <p>Error loading events: {eventsError.message}</p>
      ) : (
        <>
          {/* First section - Retail Village events only */}
          <WhatsOn 
            title={retailSettings.eventsSection?.title || "What's On at The Retail Village"} 
            events={[...retailEvents]} 
            enableScrolling={true}
            showParklandSection={false}
            emptyMessage="No upcoming events at the Retail Village. Check back soon for new events!"
          />
          
          {/* Second section - Other events (excluding Retail Village and Parkland) */}
          <WhatsOn 
            title="Don't Miss Out at Belvoir Castle" 
            events={[...otherEvents]} 
            showParklandSection={false}
            emptyMessage="No upcoming events at Belvoir Castle. Check back soon for new events!"
          />
        </>
      )}

      <ColumnX2 
        title={columnX2Title}
        description={columnX2Description}
        buttonText={columnX2ButtonText}
        buttonAction={() => navigate('/retail-village/shops')}
      />
      <div style={{ margin: '80px 0' }}>
        <NewsletterSignUp />
      </div>
      {imageSlantSections.map((section, index) => (
        <ImageSlant 
          key={index}
          image={section.image?.node?.sourceUrl || (index === 0 ? retailImage : gardensImage)}
          title={section.title}
          description={section.description}
          buttonText={section.buttonText}
          buttonAction={() => {
            // Map different sections to appropriate routes
            const routes = {
              'Visit Animal Park': '/retail-village/animal-park',
              'View Shops': '/retail-village/shops',
              'View Dining': '/retail-village/dining',
              'Learn More': '/retail-village'
            };
            navigate(routes[section.buttonText] || '/retail-village');
          }}
          imagePosition={section.imagePosition || (index % 2 === 0 ? 'left' : 'right')}
        />
      ))}
      <Footer />
    </HomeContainer>
  );
};

export default RetailVillage;

